<template>
  <div class="crawler-manager">
    <!-- Toast 提示 -->
    <transition name="toast-fade">
      <div v-if="toast.show" :class="['toast', 'toast-' + toast.type]" @click="toast.show = false">
        {{ toast.message }}
      </div>
    </transition>

    <!-- 公司信息（全局参考表） -->
    <CrawlerCompanyInfoPanel v-if="activePanel === 'crawler-company-info'" />

    <!-- 任务管理面板 -->
    <CrawlerTaskPanel
      v-if="activePanel === 'crawler-task'"
      :tasks="tasks"
      :selected-task-id="selectedTaskId"
      :selected-task="selectedTask"
      :is-loading="isLoading"
      @task-change="onTaskChange"
      @task-created="onTaskCreated"
      @task-deleted="onTaskDeleted"
      @refresh="refreshData"
      @select-task="selectTask"
      @switch-crawler-tab="$emit('switch-crawler-tab', $event)"
    />

    <!-- 数据审核面板（含精简/全列视图与导出 Excel） -->
    <CrawlerDataReview
      v-if="activePanel === 'crawler-review'"
      ref="dataReview"
      :tasks="tasks"
      :selected-task-id="selectedTaskId"
      :selected-task="selectedTask"
      :is-loading="isLoading"
      @task-change="onTaskChange"
      @results-loaded="onResultsLoaded"
    />
  </div>
</template>

<script>
import { createApiClient } from '@/services/httpSetup';
import { toastMixin } from '@/utils/toastMixin.js';
import CrawlerCompanyInfoPanel from './crawler/CrawlerCompanyInfoPanel.vue';
import CrawlerTaskPanel from './crawler/CrawlerTaskPanel.vue';
import CrawlerDataReview from './crawler/CrawlerDataReview.vue';

const apiClient = createApiClient({
  baseURL: '/api/crawler'
});

export default {
  name: 'CrawlerManager',
  mixins: [toastMixin],
  components: {
    CrawlerCompanyInfoPanel,
    CrawlerTaskPanel,
    CrawlerDataReview
  },
  props: {
    activePanel: {
      type: String,
      default: 'crawler-task'
    }
  },
  data() {
    return {
      tasks: [],
      selectedTaskId: '',
      selectedTask: null,
      isLoading: false,
      resultStats: null,
      progressInterval: null
    };
  },
  computed: {
    isTaskRunning() {
      return this.selectedTask && ['crawling', 'parsing'].includes(this.selectedTask.status);
    }
  },
  methods: {
    // 加载任务列表
    async loadTasks(autoSelectLatest = true) {  // 默认自动选中最新任务
      this.isLoading = true;
      try {
        const res = await apiClient.get('/tasks', { params: { pageSize: 100 } });
        this.tasks = res.data.data || [];
        
        // 任务已按 created_at DESC 排序，第一个就是最新的
        if (this.tasks.length > 0) {
          // 如果还没有选中任务，或者明确要求选中最新
          if (autoSelectLatest || !this.selectedTaskId) {
            const latestTask = this.tasks[0];
            this.selectedTaskId = latestTask.id.toString();
            this.selectedTask = latestTask;
            console.log(`[loadTasks] 自动选中最新任务: ${latestTask.task_name} (${latestTask.status})`);
          } else if (this.selectedTaskId) {
            // 刷新已选任务的状态
            const updatedTask = this.tasks.find(t => t.id === parseInt(this.selectedTaskId));
            if (updatedTask) {
              this.selectedTask = updatedTask;
            } else {
              // 任务可能被删除了，选中最新任务
              const latestTask = this.tasks[0];
              this.selectedTaskId = latestTask.id.toString();
              this.selectedTask = latestTask;
            }
          }
          
          // 如果任务正在运行，开始轮询
          if (this.isTaskRunning) {
            this.startProgressPolling();
          }
        } else {
          this.selectedTaskId = '';
          this.selectedTask = null;
        }
      } catch (err) {
        console.error('加载任务失败:', err);
        this.showToast('加载任务列表失败: ' + (err.response?.data?.error || err.message), 'error');
      } finally {
        this.isLoading = false;
      }
    },
    
    // 任务选择变化
    async onTaskChange(taskId) {
      this.selectedTaskId = taskId;
      
      if (!taskId) {
        this.selectedTask = null;
        this.stopProgressPolling();
        return;
      }
      
      this.selectedTask = this.tasks.find(t => t.id === parseInt(taskId));
      
      // 如果任务正在运行，开始轮询
      if (this.isTaskRunning) {
        this.startProgressPolling();
      } else {
        this.stopProgressPolling();
      }
    },
    
    // 选择任务（从任务列表对话框）
    selectTask(taskId) {
      this.selectedTaskId = taskId;
      this.onTaskChange(taskId);
    },
    
    // 任务创建成功
    async onTaskCreated(task) {
      await this.loadTasks();
      this.selectedTaskId = task.id;
      await this.onTaskChange(task.id);
    },
    
    // 任务删除成功
    async onTaskDeleted(taskId) {
      if (this.selectedTaskId === taskId) {
        this.selectedTaskId = '';
        this.selectedTask = null;
      }
      await this.loadTasks();
    },
    
    // 刷新数据（保持当前选中的任务）
    async refreshData() {
      await this.loadTasks(false);
      if (this.selectedTaskId && this.$refs.dataReview) {
        this.$refs.dataReview.loadResults();
      }
    },
    
    // 结果数据加载完成
    onResultsLoaded(stats) {
      this.resultStats = stats;
    },
    
    
    // 进度轮询 - 使用更短的间隔确保实时更新
    startProgressPolling() {
      if (this.progressInterval) return;
      
      // 立即执行一次，不等待第一个间隔
      this.pollTaskStatus();
      
      // 每3秒轮询一次（从10秒改为3秒，提升响应速度）
      this.progressInterval = setInterval(() => {
        this.pollTaskStatus();
      }, 3000);
    },
    
    async pollTaskStatus() {
      if (!this.selectedTaskId) {
        this.stopProgressPolling();
        return;
      }
      
      try {
        const res = await apiClient.get(`/tasks/${this.selectedTaskId}`);
        const previousStatus = this.selectedTask?.status;
        this.selectedTask = res.data;
        
        // 更新任务列表中的数据
        const idx = this.tasks.findIndex(t => t.id === parseInt(this.selectedTaskId));
        if (idx >= 0) {
          this.$set(this.tasks, idx, { ...this.tasks[idx], ...res.data });
        }
        
        // 如果任务状态变化了，检查是否需要停止轮询
        if (previousStatus !== res.data.status) {
          console.log(`[轮询] 任务状态变化: ${previousStatus} -> ${res.data.status}`);
          
          // 如果任务完成、失败或链接收集完成，停止轮询
          if (!['crawling', 'parsing'].includes(res.data.status)) {
            this.stopProgressPolling();
            
            // 刷新数据审核面板
            if (this.$refs.dataReview) {
              this.$refs.dataReview.loadResults();
            }
            
            // 只在状态首次变化时弹窗提示
            if (res.data.status === 'completed') {
              this.showToast('任务完成！', 'success');
            } else if (res.data.status === 'failed') {
              const msg = res.data.error_message || '';
              if (msg.includes('用户中断')) {
                this.showToast('任务已中断，可以继续处理剩余数据。', 'warning');
              } else {
                this.showToast('任务失败：' + (msg || '未知错误'), 'error');
              }
            } else if (res.data.status === 'link_collected') {
              this.showToast(`链接收集完成！共收集到 ${res.data.result_count || 0} 家公司的财报，请在下方开始解析PDF。`, 'success');
            } else if (res.data.status === 'cancelled') {
              this.showToast('任务已中断，可以点击"继续爬取链接"恢复。', 'warning');
            }
          }
        }
        
        // 如果任务不再运行，停止轮询
        if (!['crawling', 'parsing'].includes(res.data.status)) {
          this.stopProgressPolling();
        }
      } catch (err) {
        console.error('获取任务状态失败:', err);
      }
    },
    
    stopProgressPolling() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    }
  },
  
  watch: {
    // 当切换面板时，刷新任务状态
    activePanel: {
      immediate: false,
      handler() {
        // 先停止旧的轮询
        this.stopProgressPolling();
        // 重新加载任务
        this.loadTasks().then(() => {
          // 如果有正在运行的任务，启动轮询
          if (this.isTaskRunning) {
            this.startProgressPolling();
          }
        });
      }
    },
    
    // 监听选中的任务，自动启动/停止轮询
    selectedTask: {
      deep: true,
      handler(newTask, oldTask) {
        if (!newTask) {
          this.stopProgressPolling();
          return;
        }
        
        const isRunning = ['crawling', 'parsing'].includes(newTask.status);
        const wasRunning = oldTask && ['crawling', 'parsing'].includes(oldTask.status);
        
        if (isRunning && !this.progressInterval) {
          console.log('[Watch] 检测到任务正在运行，启动轮询');
          this.startProgressPolling();
        } else if (!isRunning && wasRunning) {
          console.log('[Watch] 任务已停止运行，停止轮询');
          this.stopProgressPolling();
        }
      }
    }
  },
  
  async mounted() {
    // 加载任务并自动选中最新任务
    await this.loadTasks(true);
  },
  
  beforeDestroy() {
    this.stopProgressPolling();
  }
};
</script>

<style scoped>
.crawler-manager {
  padding: 20px;
}

/* Toast 提示 */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  z-index: 9999;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  max-width: 500px;
}
.toast-success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
.toast-error { background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; }
.toast-warning { background: #fff3cd; color: #856404; border: 1px solid #ffeeba; }

.toast-fade-enter-active, .toast-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.toast-fade-enter, .toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-12px);
}
</style>
